import { BaseAction } from "../../../core/actions/BaseAction";
import { hideLoader, showLoader } from "../../../Lib/Utils/validationUtil";
import { IFulfillment } from "../../../Orchestrator/Types/Index";
import { IShareTicketParams } from "../../../Orchestrator/Types/Interface/ActionParams/IShareTicketActionParams";

export class ShareTicketAction extends BaseAction<void, IShareTicketParams> {

  getPdfUrl = (fulfillment: IFulfillment): string | undefined => {
    return fulfillment.fulfillmentDocuments?.find(
      (doc: any) => doc.medium === "PDF_A4" && doc.type === "TICKET"
    )?.downloadLink;
  }

  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  override async run(): Promise<void> {
    try {
      showLoader();
      const { booking, tripId } = this.params!;

      let fulfillments = booking?.booking?.fulfillments || [];

      if (tripId) {
        const matchBookedOffers = booking.booking.bookedOffers.find(i => i.tripCoverage.coveredTripId === tripId);
        const admissionsIds = matchBookedOffers?.admissions.map(i => i.id) || [];
        fulfillments = fulfillments.filter(i => admissionsIds.includes(i.id));
      }

      const fullfilledTickets = fulfillments.filter((fulfillment) => {
        const isFulfilled = booking.booking?.bookedOffers?.some((offer) =>
          offer.admissions.some((admission) =>
            admission.id === fulfillment.id && admission.status === "FULFILLED"
          )
        );
        return isFulfilled && this.getPdfUrl(fulfillment);
      });

      if (fullfilledTickets.length === 0) {
        console.warn('[ShareTicket] No fulfilled tickets found');
        return;
      }

      const pdfUrl = this.getPdfUrl(fullfilledTickets[0]);

      if (!pdfUrl) {
        console.warn('[ShareTicket] No PDF URL found');
        return;
      }

      // Check if Web Share is supported on mobile BEFORE fetching (while activation is valid)
      const isMobile = this.isMobileDevice();
      const canShareFiles = isMobile && 'canShare' in navigator && navigator.canShare({
        files: [new File([''], 'test.pdf', { type: 'application/pdf' })]
      });

      // Fetch the PDF blob
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const file = new File([blob], 'ticket.pdf', { type: 'application/pdf' });

      // Try Web Share on mobile if supported (activation might have expired during fetch)
      if (canShareFiles) {
        try {
          await navigator.share({ files: [file], title: 'Your Ticket' });
          return;
        } catch (error: any) {
          if (error?.name === 'AbortError') {
            return; // User cancelled - don't download
          }
          // Share failed - fall through to download
        }
      }

      // Desktop or mobile fallback: Trigger download
      const a = document.createElement('a');
      a.download = 'ticket.pdf';
      a.style.display = 'none';
      a.href = URL.createObjectURL(blob);
      a.addEventListener('click', () => {
        setTimeout(() => {
          URL.revokeObjectURL(a.href);
          a.remove();
        }, 1000);
      });
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      console.error("[ShareTicket] Error:", error);
    } finally {
      hideLoader();
    }
  }
}

export const shareTicketAction = new ShareTicketAction();
